import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function StretchingGuide() {
  const navigate = useNavigate();

  const stretches = [
    { name: "Neck Rolls", target: "Neck", duration: "30 sec each direction", steps: ["Drop chin to chest", "Slowly roll head to right shoulder", "Continue to back, then left", "Reverse direction"], caution: "Move slowly, never force" },
    { name: "Shoulder Stretch", target: "Shoulders", duration: "30 sec each side", steps: ["Bring right arm across chest", "Use left hand to press arm closer", "Feel stretch in back of shoulder", "Repeat other side"], caution: "Don't pull at elbow joint" },
    { name: "Chest Opener", target: "Chest & Shoulders", duration: "30 sec", steps: ["Stand in doorway, arms on frame", "Lean forward gently", "Feel stretch across chest", "Keep core engaged"], caution: "Don't arch lower back" },
    { name: "Standing Quad Stretch", target: "Front of Thighs", duration: "30 sec each leg", steps: ["Stand on left foot", "Grab right ankle behind you", "Keep knees close together", "Hold wall if needed"], caution: "Keep hips squared forward" },
    { name: "Seated Hamstring Stretch", target: "Back of Thighs", duration: "30 sec each leg", steps: ["Sit with one leg extended", "Bend other foot to inner thigh", "Reach toward extended foot", "Keep back straight"], caution: "Hinge at hips, not waist" },
    { name: "Hip Flexor Lunge", target: "Hip Flexors", duration: "30 sec each side", steps: ["Step into lunge position", "Lower back knee to floor", "Push hips forward gently", "Feel stretch in front of hip"], caution: "Keep front knee over ankle" },
    { name: "Child's Pose", target: "Back & Hips", duration: "60 sec", steps: ["Kneel on floor", "Sit back on heels", "Reach arms forward on ground", "Rest forehead on mat"], caution: "Use pillow under hips if needed" }
  ];

  const tips = [
    "Never bounce - hold stretches steady",
    "Breathe deeply throughout each stretch",
    "Stretch to mild discomfort, never pain",
    "Hold each stretch for at least 30 seconds",
    "Stretch when muscles are warm (after activity)",
    "Be consistent - flexibility takes time"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Week 4 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Full Body Stretch
          </h1>
          <p className="text-xl text-secondary-600">Release Tension and Improve Flexibility</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Sparkles className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Session: 15-20 Minutes</h3>
              <p className="text-teal-100 text-lg">
                Hold each stretch for 30 seconds, breathing deeply. Focus on the areas that feel tightest. 
                This routine targets the major muscle groups that get tight from sitting and daily life.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Benefits of Regular Stretching</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "Injury Prevention", desc: "Flexible muscles are less prone to strains" },
              { title: "Better Posture", desc: "Releases tension that causes slouching" },
              { title: "Reduced Pain", desc: "Eases muscle tension and joint stiffness" },
              { title: "Improved Circulation", desc: "Increases blood flow to muscles" },
              { title: "Stress Relief", desc: "Physical release helps mental relaxation" },
              { title: "Greater Range of Motion", desc: "Makes daily activities easier" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-teal-50 rounded-xl text-center">
                <h3 className="font-bold text-teal-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stretches */}
        <section className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-teal-500" />
            The Full Body Routine
          </h2>
          <div className="space-y-4">
            {stretches.map((stretch, index) => (
              <div key={index} className="bg-white rounded-xl p-5">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3">
                  <h3 className="text-lg font-bold text-teal-700">{stretch.name}</h3>
                  <span className="text-sm bg-teal-100 text-teal-600 px-2 py-1 rounded-full w-fit">{stretch.target}</span>
                  <span className="text-sm text-secondary-500">{stretch.duration}</span>
                </div>
                <ol className="grid md:grid-cols-2 gap-2 mb-3">
                  {stretch.steps.map((step, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <span className="w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {stretch.caution}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Stretching Tips</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {tips.map((tip, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                {tip}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StretchingGuide;
